import { useEffect, useRef, useState } from 'react';
import {
  ExceptionReference,
  ExceptionReferenceTypes,
  IngestFile,
  JobDetail,
  OrderDetail,
  OrderJobAsset,
  OrderJobTask,
  PolarisApiResponse
} from '@/redux/slices/slice_types';
import { isCancel } from 'axios';
import axios from '@/_utils/axios';
import { useToast } from '@/components/_common/Toast';

type PrimaryReferenceDetails =
  | { type: ExceptionReferenceTypes.Order; order: OrderDetail }
  | { type: ExceptionReferenceTypes.Job; job: JobDetail }
  | { type: ExceptionReferenceTypes.Task; task: OrderJobTask }
  | { type: ExceptionReferenceTypes.Asset; asset: OrderJobAsset }
  | { type: ExceptionReferenceTypes.Ingest; ingestFile: IngestFile };

interface PrimarySpecificRefsLoaderResult {
  isLoading: boolean;
  details: PrimaryReferenceDetails | null;
}

export const useRefDetailsLoader = (primaryReference: ExceptionReference | undefined): PrimarySpecificRefsLoaderResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [details, setDetails] = useState<PrimaryReferenceDetails | null>(null);
  const { error } = useToast();
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!primaryReference) {
      return;
    }

    setIsLoading(true);

    abortControllerRef.current = new AbortController();
    const abortController = abortControllerRef.current;

    const run = async () => {
      const id = primaryReference.id;
      const errorMsg = 'Failed to load primary reference details';
      try {
        let details: PrimaryReferenceDetails | undefined;
        switch (primaryReference.type) {
          case 'order':
            details = { type: ExceptionReferenceTypes.Order, order: await loadOrder(id, abortController) };
            break;
          case 'job':
            details = { type: ExceptionReferenceTypes.Job, job: await loadJob(id, abortController) };
            break;
          case 'task':
            details = { type: ExceptionReferenceTypes.Task, task: await loadTask(id, abortController) };
            break;
          case 'asset':
            details = { type: ExceptionReferenceTypes.Asset, asset: await loadAsset(id, abortController) };
            break;
          case 'ingest':
            details = { type: ExceptionReferenceTypes.Ingest, ingestFile: await loadIngest(id, abortController) };
            break;
        }

        if (!details) {
          throw new Error(errorMsg);
        }

        setIsLoading(false);
        setDetails(details);
      } catch (e: any) {
        setIsLoading(false);
        if (!isCancel(e)) {
          error(e.data.message || errorMsg);
        }
      }
    };

    run();
  }, [primaryReference]);

  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
    },
    []
  );

  return {
    isLoading,
    details
  };
};

const loadOrder = async (orderId: string, abortController: AbortController) => {
  return axios.get<PolarisApiResponse<OrderDetail>>(`/core/orders/${orderId}/`, { signal: abortController.signal }).then((res) => res.data.data);
};

const loadJob = async (jobId: string, abortController: AbortController) => {
  return axios.get<PolarisApiResponse<JobDetail>>(`/core/jobs/${jobId}/`, { signal: abortController.signal }).then((res) => res.data.data);
};

const loadTask = async (taskId: string, abortController: AbortController) => {
  return axios.get<PolarisApiResponse<OrderJobTask>>(`/core/tasks/${taskId}/`, { signal: abortController.signal }).then((res) => res.data.data);
};

const loadAsset = async (assetId: string, abortController: AbortController) => {
  return axios
    .get<PolarisApiResponse<OrderJobAsset>>(`/core/jobs/assets/${assetId}/`, { signal: abortController.signal })
    .then((res) => res.data.data);
};

const loadIngest = async (ingestId: string, abortController: AbortController) => {
  return axios
    .get<PolarisApiResponse<IngestFile>>(`/ingest_manager/ingest_records/${ingestId}/`, { signal: abortController.signal })
    .then((res) => res.data.data);
};
